// `publicNames`, ported from the prototype endpoint layer (`companion/src/serve.mjs:53-55` at Basil
// commit b9b62c8, served on `GET /api/episode` as `names`). SHIP-SPEC-2026-09-05 section 4: the
// surface shows the writer words, never engine ids, and a `NamesTable` carries each domain as two
// `Map`s that neither survive `JSON.stringify` nor render. This flattens the readable half — the
// `byId` map of each domain — to a plain object, and drops `byName`, which is the parser's index.
//
// It lives in the core (amendment A5) and it is deliberately one-way: a `PublicNames` value is for
// display and for a completion list. Anything that needs to look a word back up goes through
// `createNamesTable` and reads `byName`.
import type {
  NameDomainKey,
  NamesTable,
} from '../../../dorian-shared/types/episodeScript';

/** One domain flattened: the id, as a string, to the word the writer reads. */
export type PublicNameDomain = Record<string, string>;

/** The four domains of a names table, each as plain `id → word`. */
export type PublicNames = Record<NameDomainKey, PublicNameDomain>;

/**
 * A names table as plain data: each domain's `byId` map as an object, `byName` dropped.
 *
 * `Object.fromEntries` over `Object.entries(table)` is the frozen shape (serve.mjs:54); it is
 * spelled out here as a typed reduce because `Object.fromEntries` loses the key union that makes
 * the return a `Record<NameDomainKey, …>` rather than a `Record<string, …>`.
 */
export function publicNames(table: NamesTable): PublicNames {
  const keys = Object.keys(table) as NameDomainKey[];
  const result = {} as PublicNames;

  keys.forEach((key) => {
    const domain: PublicNameDomain = {};

    table[key].byId.forEach((word, id) => {
      domain[String(id)] = word;
    });
    result[key] = domain;
  });

  return result;
}
